import Alert from '@redbubble/design-system/src/Alert';
import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Stack from '@redbubble/design-system/src/Stack';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, IntlShape, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { AnalyticsPromotionCreativeSlot } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import Image from 'next/legacy/image';
import { useMemo } from 'react';
import { InView } from 'react-intersection-observer';
import { trackGA4ViewPromotion } from '../../pages/cart/utils/analytics';
import success from '../../public/icons/success.svg';
import { CartDiscountBanner } from './CartDiscountBanner';
import {
  groupDiscountMessages,
  shortenAppliedDiscountText,
} from './groupDiscountMessages';
import styles from './Messages.module.scss';
import {
  Message as MessageInterface,
  MessageSeverity,
  MessageType,
} from './types';
import { unavailableItemMessages } from './unavailableItemMessages';
import { messageSeverityToDesignSystemConstant } from './utils';

export const intlMessages = defineMessages({
  listLabel: {
    defaultMessage: 'Cart messages',
  },
  discountsAppliedHeading: {
    defaultMessage: 'Discounts applied to your cart:',
  },
  discountsAvailableHeading: {
    defaultMessage: 'Available discounts for items in your cart:',
  },
});

export interface MessagesProp {
  messages: MessageInterface[];
  hideAppliedIcons?: boolean;
}

export const getMsgText = (
  text: string,
  type: MessageType | undefined,
  intl: IntlShape
): string => {
  if (type === MessageType.NonsaleableProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.nonSaleableItem);
  }

  if (type === MessageType.ExpiredProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.expiredItem);
  }

  return text;
};

export const getActionUrl = (
  url: string,
  type: MessageType | undefined,
  locale: string
) => {
  if (type === MessageType.NonsaleableProductRemoval) {
    return url.replace(
      '/articles/',
      `/${locale === 'en' ? 'en-us' : locale}/articles/`
    );
  }

  return url;
};

const getPromoKey = (message: MessageInterface) =>
  `${message.text}_${message.type}_${message.itemId}`;

const handleInViewTracking = (message: MessageInterface) => {
  if (message.text && message.type === MessageType.DiscountSitewide) {
    trackGA4ViewPromotion({
      promotionName: message.text,
      creativeSlot: AnalyticsPromotionCreativeSlot.CartMessages,
    });
  }
};

export const Message = ({
  text,
  severity,
  actionUrl,
  showAppliedIcon,
  onScrollInView,
  onClick,
  element = 'li',
}: {
  text: string;
  severity: MessageSeverity;
  actionUrl?: string;
  showAppliedIcon?: boolean;
  onScrollInView?: () => void;
  onClick?: () => void;
  element?: 'li' | 'div';
}) => {
  const content = (
    <Stack direction={constants.HORIZONTAL} spacing="m">
      {showAppliedIcon && (
        <Image
          alt=""
          layout="fixed"
          src={success}
          width={22}
          height={24}
          aria-hidden
        />
      )}
      <Text>{text}</Text>
    </Stack>
  );

  const intent = messageSeverityToDesignSystemConstant[severity];

  return (
    <InView onChange={onScrollInView} as={element} triggerOnce>
      <Alert intent={intent} label={false}>
        {actionUrl ? (
          <a href={actionUrl} className={styles.link} onClick={onClick}>
            {content}
          </a>
        ) : (
          content
        )}
      </Alert>
    </InView>
  );
};

export const GroupedMessages = ({
  messages,
  hideAppliedIcons,
}: MessagesProp) => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const intl = useIntl();

  const { othersBefore, applied, availableItemDiscounts, othersAfter } =
    useMemo(() => groupDiscountMessages(messages), [messages]);

  if (messages.length === 0) return <div />;

  const getPromoActionUrl = (message: MessageInterface) =>
    message.actionUrl
      ? getActionUrl(message.actionUrl, message.type, locale)
      : undefined;

  return (
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(intlMessages.listLabel)}
    >
      {othersBefore.map((message) => (
        <Message
          key={getPromoKey(message)}
          element="li"
          actionUrl={getPromoActionUrl(message)}
          severity={message.severity}
          text={getMsgText(message.text || '', message.type, intl)}
          showAppliedIcon={message.applied && !hideAppliedIcons}
          onScrollInView={() => handleInViewTracking(message)}
        />
      ))}
      <CartDiscountBanner
        element="li"
        intent={constants.SUCCESS}
        heading={intl.formatMessage(intlMessages.discountsAppliedHeading)}
        messages={applied}
        showIcon={!hideAppliedIcons}
        getKey={getPromoKey}
        getLineText={(message) =>
          shortenAppliedDiscountText(
            getMsgText(message.text || '', message.type, intl)
          )
        }
        getActionUrl={getPromoActionUrl}
        onLineInView={handleInViewTracking}
      />
      <CartDiscountBanner
        element="li"
        intent={constants.INFORMATION}
        heading={intl.formatMessage(intlMessages.discountsAvailableHeading)}
        messages={availableItemDiscounts}
        getKey={getPromoKey}
        getLineText={(message) =>
          getMsgText(message.text || '', message.type, intl)
        }
        getActionUrl={getPromoActionUrl}
        onLineInView={handleInViewTracking}
      />
      {othersAfter.map((message) => (
        <Message
          key={getPromoKey(message)}
          element="li"
          actionUrl={getPromoActionUrl(message)}
          severity={message.severity}
          text={getMsgText(message.text || '', message.type, intl)}
          showAppliedIcon={message.applied && !hideAppliedIcons}
          onScrollInView={() => handleInViewTracking(message)}
        />
      ))}
    </Box>
  );
};

export const Messages = ({ messages, hideAppliedIcons }: MessagesProp) => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const intl = useIntl();

  if (messages.length === 0) return <div />;

  const getPromoActionUrl = (message: MessageInterface) =>
    message.actionUrl
      ? getActionUrl(message.actionUrl, message.type, locale)
      : undefined;

  return (
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(intlMessages.listLabel)}
    >
      {messages.map((message) => (
        <Message
          key={getPromoKey(message)}
          element="li"
          actionUrl={getPromoActionUrl(message)}
          severity={message.severity}
          text={getMsgText(message.text || '', message.type, intl)}
          showAppliedIcon={message.applied && !hideAppliedIcons}
          onScrollInView={() => handleInViewTracking(message)}
        />
      ))}
    </Box>
  );
};
